<template>
  <div
    class="Map"
    :data-panel="this.$store.state.infoPanelDeployed"
    :data-search="this.$store.state.searchBar"
    >
    <mapbox
      access-token="pk.eyJ1IjoicGxjYXJ0b25nIiwiYSI6ImNrN25pbTN4bDAycXEzZnM4a212M3k1dWkifQ.mfCBz7pz5g7ykUXaeNy13A"
      :map-options="{
        style: 'mapbox://styles/plcartong/ckbegq63f06v91io6w9n8bj1a/draft',
        center: [14, 18],
        zoom: 1.4,
        maxZoom: 6,
        interactive: true,
        fullscreenControl: false,
        renderWorldCopies: false
      }"
      :nav-control="{ show: true, position: 'top-left' }"
      @map-init="initialized"
      @map-load="loaded"
      @map-click:countries-land="clicked"
      @map-click:us-states-land="clicked"
      @map-mousemove:countries-land="mousemove"
      @map-mousemove:us-states-land="mousemove"
      @map-mouseleave:countries-land="mouseleave"
      @map-mouseleave:us-states-land="mouseleave"
    />
    <div :class="'TreatySwitch__ctn' + (this.activeNavbarItem === 'treaty' ? ' TreatySwitch__ctn--show' : '' )">
      <TreatySwitch/>
    </div>
    <div :class="'ECPMSwitch__ctn' + (this.$store.state.ecpmActionsLayer ? ' ECPMSwitch__ctn--show' : '' )">
      <ECPMSwitch/>
      <IGOSwitch/>
    </div>
    <span class="Map__hoveredPlace hide-sm" v-if="this.$store.state.hoveredPlace">{{ words[this.lang].general.PlaceLabel }} : {{ this.$dataset[this.$store.state.hoveredPlace]['placename' + this.lang] }}</span>
    <Legend />
    <InfoPanel ref="infoPanel" />
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl-vue'
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import Legend from './Legend.vue'
import TreatySwitch from './TreatySwitch.vue'
import ECPMSwitch from './ECPMSwitch.vue'
import IGOSwitch from './IGOSwitch.vue'
import InfoPanel from './InfoPanel.vue'
import { words } from '@/constants/lang'

export default {
  name: 'Map',
  components: { Mapbox, InfoPanel, Legend, TreatySwitch, ECPMSwitch, IGOSwitch },
  data: () => ({
    hoveredStateIds: null,
    ecpmActionsLayer: null,
    words,
    padding: 20,
    layers: {
      status: {
        countriesLand: "countries-land",
        statesLand: "us-states-land",
        countriesAbolitionist: "countries-abolitionist",
        statesAbolitionist: "states-abolitionist",
        countriesCommonRightAbolitionist: "countries-common-rights-abolitionist",
        statesCommonRightAbolitionist: "states-common-rights-abolitionist",
        countriesRetentionist: "countries-retentionist",
        statesRetentionist: "states-retentionist",
        countriesMoratorium: "countries-moratorium",
        statesMoratorium: "states-moratorium",
      },
      op2: {
        countriesOp2Ratified: "countries-op2-ratified",
        statesOp2Ratified: "states-op2-ratified",
        countriesOp2NotRatified: "countries-op2-not-ratified",
        statesOp2NotRatified: "states-op2-not-ratified",
      },
      igo: [],
      moratorium: {
        countriesMoratoriumInFavour: "countries-moratorium-in-favour",
        statesMoratoriumInFavour: "states-moratorium-in-favour",
        countriesMoratoriumAgainst: "countries-moratorium-against",
        statesMoratoriumAgainst: "states-moratorium-against",
        countriesMoratoriumAbstention: "countries-moratorium-abstention",
        statesMoratoriumAbstention: "states-moratorium-abstention",
        countriesMoratoriumAbsence: "countries-moratorium-absence",
        statesMoratoriumAbsence: "states-moratorium-absence"
      }
    },
    focusLayers: {
      placeFocus: "place-focus-00000000",
      placeBorderFocusShadow: "place-border-focus-shadow-00000000",
      placeBorderFocus: "place-border-focus-00000000",
    },
    ecpmOnTheSpotLayer: "countries-ecpm-actions"
  }),
  computed: {
    map: function () {
      return this.$store.state.map;
    },
    activeIgo: function () {
      return this.$store.state.activeIgo;
    },
    activeNavbarItem: function () {
      return this.$store.state.activeNavbarItem;
    },
    activeCountry: function () {
      return this.$store.state.activeCountry;
    },
    igoCountryFilter: function () {
      if (this.activeIgo != null) {
        let iso2IgoPlaces = this.convertA3ArrayToA2(this.$igoPlaces[this.activeIgo]['places'])
        return ["match", ["get", "iso_3166_1"], iso2IgoPlaces, 1, 0.1];
      }
      return 1
    },
    flyToOptions: function () {
      return {
        padding: {
          top: this.padding + 45,
          left: this.padding,
          right: this.padding + (this.$store.state.infoPanelDeployed ? 160 : 0),
          bottom: this.padding + 81
        },
        easing: function(t) {
          return t;
        },
        maxZoom: 3.9,
        maxDuration: 0
      }
    },
    hoveredStateId: function () {
      return this.hoveredStateIds
    },
    lang: function () {
      return this.$store.state.language.lang
    }
  },
  watch: {
    '$store.state.language': function() {
      this.translate()
    },
    '$store.state.activeNavbarItem': function() {
      this.clearFocusLayers()
      if (this.$store.state.activeCountry != null) this.focusCountry(this.$store.state.activeCountry)
    },
    '$store.state.treaty': function() {
      this.clearFocusLayers()
      if (this.$store.state.activeCountry != null) this.focusCountry(this.$store.state.activeCountry)
    },
    '$store.state.infoPanelDeployed': function() {
      if (!this.$store.state.infoPanelDeployed) this.unfocusCountry()
      else this.toggleNav()
    },
    '$store.state.ecpmActionsLayer': function() {
      this.toggleECPMIcon();
    },
    '$store.state.activeIgo': function() {
      this.showIgo();
      this.toggleECPMIcon();
    },
    '$store.state.abolitionistLayer': function() {
      if (typeof this.map.getLayer(this.layers.status.countriesAbolitionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.countriesAbolitionist, 'visibility', this.$store.state.abolitionistLayer ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.status.statesAbolitionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.statesAbolitionist, 'visibility', this.$store.state.abolitionistLayer ? 'visible' : 'none'); }
    },
    '$store.state.commonRightAbolitionistLayer': function() {
      if (typeof this.map.getLayer(this.layers.status.countriesCommonRightAbolitionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.countriesCommonRightAbolitionist, 'visibility', this.$store.state.commonRightAbolitionistLayer ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.status.statesCommonRightAbolitionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.statesCommonRightAbolitionist, 'visibility', this.$store.state.commonRightAbolitionistLayer ? 'visible' : 'none'); }
    },
    '$store.state.moratoireLayer': function() {
      if (typeof this.map.getLayer(this.layers.status.countriesMoratorium) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.countriesMoratorium, 'visibility', this.$store.state.moratoireLayer ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.status.statesMoratorium) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.statesMoratorium, 'visibility', this.$store.state.moratoireLayer ? 'visible' : 'none'); }
    },
    '$store.state.retentionistLayer': function() {
      if (typeof this.map.getLayer(this.layers.status.countriesRetentionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.countriesRetentionist, 'visibility', this.$store.state.retentionistLayer ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.status.statesRetentionist) !== 'undefined') { this.map.setLayoutProperty(this.layers.status.statesRetentionist, 'visibility', this.$store.state.retentionistLayer ? 'visible' : 'none'); }
    },

    '$store.state.moratoriumInFavourLayer': function() {
      if (typeof this.map.getLayer(this.layers.moratorium.countriesMoratoriumInFavour) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.countriesMoratoriumInFavour, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumInFavourLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.moratorium.statesMoratoriumInFavour) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.statesMoratoriumInFavour, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumInFavourLayer) ? 'visible' : 'none'); }
    },
    '$store.state.moratoriumAgainstLayer': function() {
      if (typeof this.map.getLayer(this.layers.moratorium.countriesMoratoriumAgainst) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.countriesMoratoriumAgainst, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAgainstLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.moratorium.statesMoratoriumAgainst) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.statesMoratoriumAgainst, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAgainstLayer) ? 'visible' : 'none'); }
    },
    '$store.state.moratoriumAbstentionLayer': function() {
      if (typeof this.map.getLayer(this.layers.moratorium.countriesMoratoriumAbstention) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.countriesMoratoriumAbstention, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAbstentionLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.moratorium.statesMoratoriumAbstention) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.statesMoratoriumAbstention, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAbstentionLayer) ? 'visible' : 'none'); }
    },
    '$store.state.moratoriumAbsenceLayer': function() {
      if (typeof this.map.getLayer(this.layers.moratorium.countriesMoratoriumAbsence) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.countriesMoratoriumAbsence, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAbsenceLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.moratorium.statesMoratoriumAbsence) !== 'undefined') { this.map.setLayoutProperty(this.layers.moratorium.statesMoratoriumAbsence, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.moratoriumAbsenceLayer) ? 'visible' : 'none'); }
    },
    '$store.state.op2RatifiedLayer': function() {
      if (typeof this.map.getLayer(this.layers.op2.countriesOp2Ratified) !== 'undefined') { this.map.setLayoutProperty(this.layers.op2.countriesOp2Ratified, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.op2RatifiedLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.op2.statesOp2Ratified) !== 'undefined') { this.map.setLayoutProperty(this.layers.op2.statesOp2Ratified, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.op2RatifiedLayer) ? 'visible' : 'none'); }
    },
    '$store.state.op2NotRatifiedLayer': function() {
      if (typeof this.map.getLayer(this.layers.op2.countriesOp2NotRatified) !== 'undefined') { this.map.setLayoutProperty(this.layers.op2.countriesOp2NotRatified, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.op2NotRatifiedLayer) ? 'visible' : 'none'); }
      if (typeof this.map.getLayer(this.layers.op2.statesOp2NotRatified) !== 'undefined') { this.map.setLayoutProperty(this.layers.op2.statesOp2NotRatified, 'visibility', (this.activeNavbarItem === "treaty" && this.$store.state.op2NotRatifiedLayer) ? 'visible' : 'none'); }
    }
  },
  methods: {

    initialized(map) {
      this.$store.commit('updateMap', map)
      this.toggleNav()

      // disable map rotation using right click + drag
      map.dragRotate.disable();

      // disable map rotation using touch rotation gesture
      map.touchZoomRotate.disableRotation();
    },

    addECPMIcon() {
      this.map.loadImage(
        require("@/assets/icons/ecpm_map_icon.png"),
        (error, image) => {
          if (error) throw error;

          // Add the image to the map style.
          this.map.addImage('ecpm-icon', image);
          this.map.setLayoutProperty('country-label', 'icon-image', 'ecpm-icon');
          this.map.setLayoutProperty('country-label', 'icon-anchor', 'bottom');
          this.map.setLayoutProperty('country-label', 'icon-allow-overlap', true);
          let iso2Array = this.convertA3ArrayToA2(this.$ecpmProjects);
          const ecpmFilter = iso2Array.length === 0 ? 1 : [
            'interpolate', ['linear'], ['zoom'],
             2, ["match", ["get", "iso_3166_1"], iso2Array, 0.165, 0.0001],
             4, ["match", ["get", "iso_3166_1"], iso2Array, 0.25, 0.0001],
             8, ["match", ["get", "iso_3166_1"], iso2Array, 0.3, 0.0001]
          ];
          this.map.setLayoutProperty('country-label', 'icon-size', ecpmFilter);
          this.map.setLayoutProperty('country-label', 'text-anchor', ["match", ["get", "iso_3166_1"], iso2Array, 'top', 'center']);
          this.map.setPaintProperty('country-label', 'icon-translate', [0, -7]);
          this.toggleECPMIcon()
        }
      )
    },

    toggleECPMIcon() {
      this.map.setPaintProperty('country-label', 'icon-opacity', this.$store.state.ecpmActionsLayer ? this.igoCountryFilter : 0);
    },

    translate() {
      let iso2Array = this.activeIgo != null ? this.convertA3ArrayToA2(this.$igoPlaces[this.activeIgo]['places']) : [];
      var countryLabelFilter = [];

      if (this.$store.state.language.lang === "fr") {
        countryLabelFilter = [
          'match', ['get', 'iso_3166_1'], 'BY', 'Bélarus',
          ['match', ['get', 'iso_3166_1'], 'EH', '', ['get', 'name_fr']]
        ];
      } else {
        countryLabelFilter = [
          'match', ['get', 'iso_3166_1'], 'TR', 'Türkiye',
          'match', ['get', 'iso_3166_1'], 'EH', '', ['get', 'name_' + this.$store.state.language.lang]
        ]
      }
      // ['get', 'name_' + this.$store.state.language.lang]
      let poiLabelsFilter = ['format', countryLabelFilter, { 'font-scale': iso2Array.length > 0 ? ["match", ["get", "iso_3166_1"], iso2Array, 1, 0.01] : 1 },'\n'];

      this.map.setLayoutProperty('country-label', 'text-field', poiLabelsFilter);

      this.hidePoiLabels(iso2Array)
      this.$store.state.geocoder.setLanguage(this.lang);
      this.$store.state.geocoder.setPlaceholder(words[this.lang].general.GeocoderPlaceholder);
    },

    hidePoiLabels (iso2Array) {
      let labelLayers = ['settlement-major-label', 'state-label', 'settlement-subdivision-label', 'settlement-minor-label']
      let filter = iso2Array.length === 0 ? 1 : ["match", ["get", "iso_3166_1"], iso2Array, 1, 0.0001];

      labelLayers.forEach(labelLayer => {
        this.map
          .setLayoutProperty(labelLayer, 'text-field', ['format', ['get', 'name_' + this.$store.state.language.lang], { 'font-scale': filter }, '\n'])
          .setLayoutProperty(labelLayer, 'icon-size', filter);
      })
    },

    loaded(map) {
      var self = this;
      let state = this.$store.state;

      window.addEventListener('resize', () => {
        map.resize()
      });

      // Setup the geocoder to search for places
      var geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        flyTo: this.flyToOptions,
        placeholder: words[this.lang].general.GeocoderPlaceholder,
        types: "country,region",

        // Filter results on countries or US states
        filter: function(item) {
          if (item.properties.short_code === undefined) return false;
          return (item.id.split('.').shift() === 'country' && item.properties.short_code !== "us") || (item.properties.short_code.split('-').shift() === 'US')
        },
        language: this.lang,
        marker: false, // Do not use the default marker style
      })

      map.addControl(geocoder, 'top-right');

      geocoder.on('result', function(e) {
        let iso = e.result.properties.short_code;
        let isoId = iso.includes("-") ? iso : self.getA3(iso);
        self.focusCountry(isoId);
        self.showCountry(isoId);
        let searchBar = document.querySelector('.mapboxgl-ctrl-geocoder input');
        searchBar.blur()
      });

      this.$store.commit('updateGeocoder', geocoder)

      // Translate map on load, only if it's not basemap language
      if (this.$store.state.language.lang !== "en") this.translate()

      // Adds the GeoJSON country source
      map.addSource('countries-land-10km', {
        'type': 'geojson',
        'data': 'data/countries-land-10km.geo.json',
        'attribution': `<span class="Sources" data-en=" : The data was gathered through ECPM and its partners reports, and from the Amnesty International annual report on the death penalty" data-fr=" : Les données sont issues des différents rapports d'ECPM et de ses partenaires et du rapport annuel sur la peine de mort d'Amnesty International">Sources</span> | Developed by <a href="https://cartong.org/" target="_blank">CartONG</a>`
      });

      // Adds the GeoJSON states source
      map.addSource('states-land', {
        'type': 'geojson',
        'data': 'data/us-states.geo.json'
      });

      this.addLayer(this.layers.status.countriesRetentionist, "country", this.$COLORS[1],this.$statusPlaces.Retentionist.countries, state.retentionistLayer)
      this.addLayer(this.layers.status.statesRetentionist, "state", this.$COLORS[1],this.$statusPlaces.Retentionist.states, state.retentionistLayer)

      this.addLayer(this.layers.status.countriesMoratorium, "country", this.$COLORS[2],this.$statusPlaces.Moratorium.countries, state.moratoireLayer)
      this.addLayer(this.layers.status.statesMoratorium, "state", this.$COLORS[2],this.$statusPlaces.Moratorium.states, state.moratoireLayer)

      this.addLayer(this.layers.status.countriesCommonRightAbolitionist, "country", this.$COLORS[3],this.$statusPlaces.CommonRightAbolitionist.countries, state.commonRightAbolitionistLayer)
      this.addLayer(this.layers.status.statesCommonRightAbolitionist, "state", this.$COLORS[3],this.$statusPlaces.CommonRightAbolitionist.states, state.commonRightAbolitionistLayer)

      this.addLayer(this.layers.status.countriesAbolitionist, "country", this.$COLORS[4],this.$statusPlaces.Abolitionist.countries, state.abolitionistLayer)
      this.addLayer(this.layers.status.statesAbolitionist, "state", this.$COLORS[4],this.$statusPlaces.Abolitionist.states, state.abolitionistLayer)

      this.showTreatyLayers()
      this.addECPMIcon()

      setTimeout(() => {
        if (this.activeIgo != null) { this.showIgo() }
        if (this.$store.state.activeCountry != null) { this.focusCountry(self.$store.state.activeCountry) }
      }, 1500)
    },

    createIgoPlacesLayer () {
      // If country or state igo layer doesn't exist in this.layers then add it to keep a track on created layers
      this.createIgoPlaceLayer(this.activeIgo, "country")
      this.createIgoPlaceLayer(this.activeIgo, "state")
    },

    showIgo () {
      let state = this.$store.state;

      // if country is not in active IGO, then reset it
      if (state.activeIgo != null && !this.$igoPlaces[state.activeIgo]['places'].includes(state.activeCountry)) this.unfocusCountry(false)

      if (typeof state.map.getSource("point") !== 'undefined') this.updateLayerEcpm()
      // If igo layers doesn't exists, create it
      if (state.activeIgo != null && typeof state.map.getLayer(state.activeIgo + "-country") == 'undefined') this.createIgoPlacesLayer()

      // Hides alls igo layers that exists, except active one.
      this.layers.igo.forEach(igoLayer => {
        if (typeof state.map.getLayer(igoLayer) !== 'undefined') {
          if (!igoLayer.includes(state.activeIgo))
            // state.map.setLayoutProperty(igoLayer, 'visibility', 'none');
            setTimeout(function () { state.map.setLayoutProperty(igoLayer, 'visibility', 'none'); }, 1000)
          else
            state.map.setLayoutProperty(igoLayer, 'visibility', 'visible');
        }
      });

      this.translate()

      // Zoom on igo bounds or reset zoom
      state.activeIgo !== null ? this.zoomIgoBounds() : this.resetMapZoom()
    },

    createIgoPlaceLayer (layer, type) {
      // If country or state igo layer doesn't exist in this.layers then add it to keep a track on created layers
      let placeLayer =  layer + "-" + type
      if (!this.layers.igo.includes(placeLayer)) this.layers.igo.push(placeLayer)
      this.addLayer(placeLayer, type, this.$COLORS['nonIgo'], this.$igoPlaces[this.activeIgo]['places'], true, true)

      this.setEventsOnDisabledPlace(placeLayer)
    },

    setEventsOnDisabledPlace (placeLayer) {
      let map = this.map;
      map.on('mousemove', placeLayer, function (e) {
        if (e.features.length > 0) {
          map.getCanvas().style.cursor = "";
        }
      });
      map.on('click', placeLayer, function (e) {
        if (e.features.length > 0) {
          e.preventDefault();
        }
      });
    },

    addLayer (id, type, color, placesArray, visibility, reverse = false) {
      if (placesArray.length >= 1) {
        let map = this.map;
        let config = type === "country" ? {source: 'countries-land-10km', filter: 'A3', lowerLayer: 'countries-border'} : {source: 'states-land', filter: 'name', lowerLayer: 'us-states-border' }

        map.addLayer({
            "id": id, "type": "fill", "source": config.source, "layout": {'visibility': visibility ? 'visible' : 'none'},
            "paint": { "fill-color": color },
            "maxZoom": 6,
            "filter": ['match', ['get', config.filter], placesArray, !reverse, reverse]
          },
          config.lowerLayer
        );
      }
    },

    showTreatyLayers() {
      let state = this.$store.state;
      let treaty = state.activeNavbarItem === "treaty";

      this.addLayer(this.layers.moratorium.countriesMoratoriumInFavour, "country", this.$COLORS["Oui"],this.$moratoriumPlaces.InFavour.countries, state.moratoriumInFavourLayer && treaty)
      this.addLayer(this.layers.moratorium.statesMoratoriumInFavour, "state", this.$COLORS["Oui"],this.$moratoriumPlaces.InFavour.states, state.moratoriumInFavourLayer && treaty)

      this.addLayer(this.layers.moratorium.countriesMoratoriumAgainst, "country", this.$COLORS["Non"],this.$moratoriumPlaces.Against.countries, state.moratoriumAgainstLayer && treaty)
      this.addLayer(this.layers.moratorium.statesMoratoriumAgainst, "state", this.$COLORS["Non"],this.$moratoriumPlaces.Against.states, state.moratoriumAgainstLayer && treaty)

      this.addLayer(this.layers.moratorium.countriesMoratoriumAbstention, "country", this.$COLORS["Abstention"],this.$moratoriumPlaces.Abstention.countries, state.moratoriumAbstentionLayer && treaty)
      this.addLayer(this.layers.moratorium.statesMoratoriumAbstention, "state", this.$COLORS["Abstention"],this.$moratoriumPlaces.Abstention.states, state.moratoriumAbstentionLayer && treaty)

      this.addLayer(this.layers.moratorium.countriesMoratoriumAbsence, "country", this.$COLORS["Absent"],this.$moratoriumPlaces.Absence.countries, state.moratoriumAbsenceLayer && treaty)
      this.addLayer(this.layers.moratorium.statesMoratoriumAbsence, "state", this.$COLORS["Absent"],this.$moratoriumPlaces.Absence.states, state.moratoriumAbsenceLayer && treaty)

      this.addLayer(this.layers.op2.countriesOp2Ratified, "country", this.$COLORS["x"],this.$op2Places.Ratified.countries, state.op2RatifiedLayer && treaty)
      this.addLayer(this.layers.op2.statesOp2Ratified, "state", this.$COLORS["x"],this.$op2Places.Ratified.states, state.op2RatifiedLayer && treaty)

      this.addLayer(this.layers.op2.countriesOp2NotRatified, "country", this.$COLORS[""],this.$op2Places.NotRatified.countries, state.op2NotRatifiedLayer && treaty)
      this.addLayer(this.layers.op2.statesOp2NotRatified, "state", this.$COLORS[""],this.$op2Places.NotRatified.states, state.op2NotRatifiedLayer && treaty)
    },

    /**
     * Shows the hovered place in a small box in top left corner of the map
     * @param map Map element
     * @param e Event
     */
    mousemove(map, e) {
      if (e.features.length > 0) {
        map.getCanvas().style.cursor = "pointer";
        this.$store.commit('updateHoveredPlace', e.features[0].properties.A3)
      }
    },

    /**
     * Resets the small box in top left corner of the map
     * @param map Map element
     */
    mouseleave(map) {
      map.getCanvas().style.cursor = null;
      this.$store.commit('updateHoveredPlace', '')
    },

    clicked(map, e) {
      console.log(map.getBounds())
      if (e.features) {
        let iso = e.features[0].properties.A3;

        // Stop function if Igo filter is active and clicked country is not clickable
        if (this.activeIgo != null && !this.$igoPlaces[this.activeIgo]['places'].includes(iso)) return;

        // Fly to the calculated boundaries
        let geometry = this.getBounds(e.features[0].geometry)

        // Focus on the clicked country and shows its information
        this.focusCountry(iso, geometry);
        this.showCountry(iso);
      }
    },

    /**
     * Zooms on selected IGO countries
     */
    zoomIgoBounds () {
      let bounds = this.getMultipleFeaturesBounds(this.getIgoSelectedFeatures())
      let map = this.map

      // Specific case for ASEAN
      if (this.activeIgo === "ASEAN") bounds = [{ lng: 161, lat: 33 },{ lng: 63, lat: -26 }]

      map.fitBounds(bounds, this.flyToOptions)
    },

    /**
     * Get features of selected country
     */
    getActiveCountryFeature (iso = null) {
      return this.map.querySourceFeatures('countries-land-10km', { sourceLayer: 'countries-land', filter: ['match', ['get','A3'], iso ?? this.$store.state.activeCountry, true, false]});
    },

    /**
     * Get features of selected IGO countries
     */
    getIgoSelectedFeatures () {
      return this.map.querySourceFeatures('countries-land-10km', { sourceLayer: 'countries-land', filter: ['match', ['get','A3'], this.$igoPlaces[this.activeIgo]['places'], true, false]});
    },

    /**
     * Returns extent bounds of multiple bounds
     * @param array features Contains an array of features to calculate the extent bounds on
     */
    getMultipleFeaturesBounds (features) {
      let calcBounds = {
        _ne: {
          lng: -270,
          lat: -270
        },
        _sw: {
          lng: 270,
          lat: 270
        },
      };

      features.forEach(feature => {
        // Get country bounds
        let bounds = this.getBounds(feature.geometry)

        // If a new extent on North, east, south, west is found then update the bounds
        if (bounds._ne.lng > calcBounds._ne.lng) calcBounds._ne.lng = bounds._ne.lng
        if (bounds._ne.lat > calcBounds._ne.lat) calcBounds._ne.lat = bounds._ne.lat
        if (bounds._sw.lng < calcBounds._sw.lng) calcBounds._sw.lng = bounds._sw.lng
        if (bounds._sw.lat < calcBounds._sw.lat) calcBounds._sw.lat = bounds._sw.lat
      })

      return mapboxgl.LngLatBounds.convert([
        [calcBounds._sw.lng, calcBounds._sw.lat],
        [calcBounds._ne.lng, calcBounds._ne.lat]
      ])
    },

    /**
     * Returns the extent boundaries of a feature geometry
     * @param Object geometry from a e.features[0] element
     */
    getBounds (geometry) {
      let coordinates = geometry.type === "MultiPolygon" ? geometry.coordinates[0][0] : geometry.coordinates[0];
      return coordinates.reduce(function (bounds, coord) {
          return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
    },

    /**
     * Returns the hex color of the given country
     * @param string iso3 String which contains the ISO 3 code of the country
     */
    getColor(iso3, type = 'status') {
      return (type === 'op2' && this.$dataset[iso3][type] == undefined) ? this.$COLORS[""] : this.$COLORS[this.$dataset[iso3][type]]
    },

    getActiveLayerGroup() {
      if (this.activeNavbarItem === "status") {
        return "status"
      } else {
        if (this.activeNavbarItem === "treaty") {
          if (this.$store.state.treaty === "moratorium")
            return "moratorium"
          else if (this.$store.state.treaty === "op2")
            return "op2"
        }
      }
    },

    /**
     * Resets the focus of the map and unselects the country
     */
    unfocusCountry(zoom = true) {

      let map = this.map,
          layers = this.layers[this.getActiveLayerGroup()];

      // Removes the focus map layers
      this.clearFocusLayers()

      // Resets layers opacity back to 1
      for (var key in layers) {
        if (typeof map.getLayer(layers[key]) !== 'undefined') {
          map.setPaintProperty(layers[key], 'fill-opacity', 1);
        }
      }

      // Hides the country sheet
      this.$store.commit('updateActiveCountry', null)
      this.toggleNav()

      if (zoom) this.resetMapZoom()
    },

    /**
     * Resets map to initial zoom
     */
    resetMapZoom () {
      let map = this.map

      map.once('moveend', () => {
        if (this.activeIgo != null) {
          this.zoomIgoBounds()
        } else {
          map.flyTo({
            center: [14, 18],
            zoom: 1.4,
            essential: true
          });
        }
      });
    },

    /**
     * Removes the layers used to highlight the selected place
     */
    clearFocusLayers() {
      let map = this.map;

      // Remove the layers
      if (typeof this.map.getLayer(this.focusLayers.placeFocus) !== 'undefined') { map.removeLayer(this.focusLayers.placeFocus); }
      if (typeof this.map.getLayer(this.focusLayers.placeBorderFocusShadow) !== 'undefined') { map.removeLayer(this.focusLayers.placeBorderFocusShadow); }
      if (typeof this.map.getLayer(this.focusLayers.placeBorderFocus) !== 'undefined') { map.removeLayer(this.focusLayers.placeBorderFocus); }

      // Add a hash to the end of the layers id, to prevent the blink effect of paint property happening before removal of the layer
      let hash = (+new Date).toString(36);
      this.focusLayers.placeFocus = this.focusLayers.placeFocus.slice(0, -8) + hash;
      this.focusLayers.placeBorderFocusShadow = this.focusLayers.placeBorderFocusShadow.slice(0, -8) + hash;
      this.focusLayers.placeBorderFocus = this.focusLayers.placeBorderFocus.slice(0, -8) + hash;
    },

    /**
     * Highlight the given place on the map
     * @param string iso String which contains the ISO 3 code of the country
     */
    focusCountry(iso, bounds = null) {
      let map = this.map,
          activeLayerGroup = this.getActiveLayerGroup(),
          layers = this.layers[activeLayerGroup];
      let source, filter, zindex;

      if (bounds === null) bounds = this.getMultipleFeaturesBounds(this.getActiveCountryFeature(iso))
      map.fitBounds(bounds, this.flyToOptions)

      // Sets the configuration of the layers depending on either it's a state or a country
      if (iso.includes("-")) {
        source = "states-land";
        filter = ['match', ['get', 'name'], [this.$dataset[iso].placenameen], true, false];
        zindex = 'us-states-border';
      } else {
        source = "countries-land-10km";
        filter = ['match', ['get', 'A3'], [iso], true, false];
        zindex = 'countries-border';
      }

      // If the focus layer exists, remove the existing ones
      if (typeof map.getLayer(this.focusLayers.placeFocus) !== 'undefined') {
        this.clearFocusLayers()
      }
      // If not, lower the opacity of other map layers
      else {
        for (var key in layers) {
          if (typeof map.getLayer(layers[key]) !== 'undefined') {
            map.setPaintProperty(layers[key], 'fill-opacity', 0.85);
          }
        }
      }

      let color = this.getColor(iso, activeLayerGroup);

      // Adds a shadow effect on the highlighted place
      map.addLayer({
          "id": this.focusLayers.placeBorderFocusShadow, "type": "line", "source": source,
          'layout': {
            'line-join': 'round',
            'line-cap': 'round'
          },
          "paint": {
              "line-color": color,
              "line-width": 16,
              "line-blur": 50,
              "line-opacity": 0.8
          },
          "filter": filter
        },
        zindex
      );

      // Fills the highlighted place with full opacity
      map.addLayer({
          "id": this.focusLayers.placeFocus, "type": "fill", "source": source, "layout": {},
          "paint": { "fill-color": color },
          "filter": filter
        }, zindex
      );

      // Adds a bigger border on the highlighted place
      map.addLayer({
          "id": this.focusLayers.placeBorderFocus, "type": "line", "source": source,
          'layout': {
            'line-join': 'round',
            'line-cap': 'round'
          },
          'paint': {
            'line-color': '#fff',
            'line-width': 1.8,
          },
          "filter": filter
        },
        'poi-label'
      );
    },

    /**
     * Triggers the country sheet update and shows the info panel
     * @param string iso String which contains the ISO 3 code of the country
     */
    showCountry(iso) {
      this.$store.commit('updateActiveCountry', iso)
      this.$store.commit('toggleInfoPanel', true)
    },

    /**
     * Converts array of ISO3 to array of ISO2 (to query mapbox country-label A2 metadata)
     * @param array array Array which contains ISO3 of countries ['FRA', 'BEL', ...]
     */
    convertA3ArrayToA2: function (array) {
      let iso2Array = [];
      array.forEach(iso3 => {
        iso2Array.push(this.$dataset[iso3].iso2)
      })
      return iso2Array;
    },

    /**
     * Returns the ISO3 of a given ISO2 (used to convert geocoder results to ISO3)
     * @param string alpha2 String which contains the ISO 2 code of the country
     */
    getA3: function (alpha2) {
      alpha2 = alpha2.toUpperCase();
      for (const [key, country] of Object.entries(this.$dataset)) {
        console.log(key)
        if (country['iso2'] === alpha2) { return country['iso']; }
      }
      return 'Not found';
    },

    /**
     * Animates the map to change it's offset, when country sheet is enabled
     */
    toggleNav: function () {
      this.map.easeTo({
        padding: this.flyToOptions.padding,
        duration: 300 // In ms, CSS transition duration property for the sidebar matches this value
      });
    },

    /**
     * Loads ECPM on the spot icons on the map
     */
    toggleEcpm: function () {

      let map = this.map;
      let centerCoords = [];
      let bounds = null;
      let self = this;

      // Gets all features where ECPM has actions in
      let reducedFeatures = map.queryRenderedFeatures({ layers: ['countries-border'], 'filter':['match', ['get', 'A3'], this.$ecpmProjects, true, false]})

      // For each of these places find their center and push it to an array
      reducedFeatures.forEach(feature => {
        if (feature) {
          var coordinates = feature.geometry.type === "MultiPolygon" ? feature.geometry.coordinates[0][0] : feature.geometry.coordinates[0];
          bounds = coordinates.reduce(function (bounds, coord) {
              return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

          // Compare features to keep the center of the biggest
          // eg. To have only one ECPM point, for example : French Guyana (FRA) < France (FRA) <-- to keep
          if (feature.properties.A3 in centerCoords) {
            if (coordinates.length > centerCoords[feature.properties.A3].coordinates.length) {
              centerCoords[feature.properties.A3] = { 'center': bounds.getCenter(), 'coordinates': coordinates };
            }
          } else {
            centerCoords[feature.properties.A3] = { 'center': bounds.getCenter(), 'coordinates': coordinates };
          }
        }
      });

      // Fix center coordinates for some countries
      let arrayFix = {
        'NOR' : {lng: 8.53, lat: 60.84},
        'GMB' : {lng: -16, lat: 13.6},
        'BEL' : {lng: 4.45, lat: 51},
        'CAN' : {lng: -111.5, lat: 55.95}
      }

      for(var iso in arrayFix) {
        if (iso in centerCoords) {
          centerCoords[iso].center.lng = arrayFix[iso].lng;
          centerCoords[iso].center.lat = arrayFix[iso].lat;
        }
      }

      // Load the ECPM icon based on calculated coordinates
      map.loadImage(
        require('../assets/icons/ecpm_new_map.png'),
        function(error, image) {
          if (error) throw error;
          map.addImage('ecpm', image);
          var ecpmPointsArray = [];

          // Add each coordinates as a GeoJSON point
          for(var iso in centerCoords) {
            ecpmPointsArray.push({
              'type': 'Feature',
              'geometry': {
                'type': 'Point',
                'coordinates': [centerCoords[iso].center.lng, centerCoords[iso].center.lat]
              },
              'properties': {'iso': iso}
            })
          }

          // Add the source
          map.addSource('point', {
            'type': 'geojson',
            'data': {
              'type': 'FeatureCollection',
              'features': ecpmPointsArray
            }
          });

          self.updateLayerEcpm()
        }
      );
    },
  },
}
</script>

<style lang="less">

@import "../less/_var.less";

.mapboxgl-ctrl-attrib {
  max-width: 445px;
}

html[lang=en] .mapboxgl-ctrl-attrib:hover .Sources::after { content: attr(data-en); }
html[lang=fr] .mapboxgl-ctrl-attrib:hover .Sources::after { content: attr(data-fr); }
.mapboxgl-ctrl.mapboxgl-ctrl-attrib:hover {
  background-color: rgba(255, 255, 255, 0.87);
}
a.mapboxgl-ctrl-logo {
  margin: 0 !important;
}
.mapboxgl-ctrl-top-right, .TreatySwitch__ctn {
  top: 0;
  left: 0;
  width: 100%;
  transition: width .3s ease-in-out;

  .mapboxgl-ctrl-geocoder {
    margin: 15px auto;
    float: inherit;
    transition: transform .2s ease-in-out;
    transform: translateY(-60px);
  }
}
.mapboxgl-ctrl-compass {
  display: none !important;
}
.mapboxgl-ctrl-top-left {
  bottom: 93px;
  top: inherit;
}
.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl, .mapboxgl-ctrl-attrib a  {
  font-weight: 100;
  color: #444;
  font-size: 11px;
  text-align: right;
}
.mapbox-improve-map { display: none; }

.TreatySwitch__ctn {
  width: 100%;
  position: relative;
  transition: width .2s ease-in-out, transform .3s ease-out, opacity .3s linear;
  transform: translateY(120px);
  opacity: 0;

  &--show {
    transform: translateY(0px);
    opacity: 1;
  }
}

.mapbox-gl-geocoder--error.mapbox-gl-geocoder--no-results {
  padding: 10px 15px;
}

.Map {
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: #fafafa;

  &[data-panel=true] {
    .mapboxgl-ctrl-top-right, .Map__ctn, .TreatySwitch__ctn {
      width: calc(100% - 380px);
    }
  }

  &[data-search=true] {
    .mapboxgl-ctrl-geocoder {
      transform: translateY(0);
    }
  }

  &__hoveredPlace {
    position: absolute;
    bottom: 92px;
    left: 36px;
    text-align: left;
    margin-left: 15px;
    font-size: 1em;
    color: #5a5a5a;
    text-shadow: 0 3px 20px rgba(0, 0, 0, 0.3);
    font-family: monospace;
    padding: 2px 5px;
    border-radius: 5px;
    background: rgba(255,255,255,0.6);
  }


  .mapboxgl-ctrl-attrib {
    position: relative;
    bottom: 81px;
  }
}
.mapboxgl-ctrl-top-right {
  z-index: 3;
}

.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl {
  position: relative;
}
.mapboxgl-ctrl-geocoder, .mapboxgl-map {
  font-family: inherit;
}
.mapboxgl-ctrl-geocoder--input {
  font-size: 0.85em;
  font-weight: 100;
  height: 39px;
}
.mapboxgl-ctrl-geocoder, .mapboxgl-ctrl-geocoder .suggestions {
  box-shadow: 0 3px 14px -3px rgba(0,0,0,.15);
}
#map {
  width: 100%;
  height: 100%;
  background: #fdfdfd;

  canvas {
    background: #fdfdfd;
  }
}
.Marker {
  background-image: url("../assets/icons/ecpm.svg");
  background-size: cover;
  width: 50px;
  height: 50px;
  cursor: pointer;
}

@media screen and (max-width: 640px) {

  .mapboxgl-ctrl-geocoder--input {
    height: 50px;
  }
  .mapboxgl-ctrl-top-right .mapboxgl-ctrl-geocoder {
    margin-top: 0;
    border-radius: 0;

    .suggestions {
      top: 100%;
      border-top-right-radius: 0;
      border-top-left-radius: 0;
    }
  }
  .mapboxgl-ctrl-top-left {
    top: calc(50% - 68px);
  }

  .Map {
    &__hoveredPlace {
      top: 15px;
    }
    &[data-panel=true] {
      .mapboxgl-ctrl-top-right, .Map__ctn, .TreatySwitch__ctn {
        width: calc(100%);
      }
    }
  }
}
</style>
